import React from 'react';
import { COLORS, TYPOGRAPHY } from '../../lib/styleGuide';

const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';

const cottageFoodPoints = [
  {
    text: '**Good fit if:** baked goods, candies, dry mixes, certain jams/jellies.',
    icon: '🍪'
  },
  {
    text: 'Make food in your **home kitchen**.',
    icon: '🏠'
  },
  {
    text: 'Follow **labeling** rules (ingredients + allergens).',
    icon: '🏷️'
  },
  {
    text: 'May need a **food handler card** and simple registration (varies by state).',
    icon: '📋'
  }
];

const notCottagePoints = [
  {
    text: '**Use this if:** hot meals, refrigerated or frozen items, dairy/egg sauces, meats.',
    icon: '🍲'
  },
  {
    text: 'Cook in a **commercial kitchen** or as a **personal chef** in the customer\'s home.',
    icon: '👨‍🍳'
  },
  {
    text: 'Expect permits/inspection for commercial kitchens; personal chef typically lighter requirements.',
    icon: '🔍'
  },
  {
    text: 'Delivery/pickup rules vary locally.',
    icon: '🚚'
  }
];

const considerationsPoints = [
  {
    text: '**Your menu:** do any items require refrigeration? If yes → Not Cottage path.',
    icon: '📋'
  },
  {
    text: '**Where you\'ll cook:** home kitchen vs. commissary vs. client\'s kitchen.',
    icon: '🏠'
  },
  {
    text: '**Speed to launch:** Cottage is usually fastest; commercial kitchen takes more setup.',
    icon: '⚡'
  },
  {
    text: '**Growth:** Commercial kitchens support more menu types and scaling.',
    icon: '📈'
  }
];

export default function NotSureYetGuidanceStep({ onNext, onBack, isFirst, isLast }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minHeight: '80vh' }}>
      {/* Header Section */}
      <div>
        <h2 style={{ fontWeight: 700, fontSize: TYPOGRAPHY.ONBOARDING.H2, margin: 0, color: '#222' }}>
          Not sure yet? No problem.
        </h2>
        <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: '10px 0 0 0', lineHeight: 1.5 }}>
          Here's a quick guide to help you decide. You can <strong>continue setting up your shop now</strong> and finalize your path later.
        </p>
      </div>

      {/* Legal Path Dependency Callout */}
      <div style={{
        background: '#F8F9FA',
        borderRadius: 12,
        padding: 16,
        border: '1px solid #E9ECEF',
        borderLeft: `4px solid ${COLORS.PRIMARY_GRADIENT_START}`
      }}>
        <p style={{ color: '#495057', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.6 }}>
          Your legal path depends on whether your food is <strong>non-perishable (shelf-stable)</strong> or <strong>perishable (needs refrigeration/heating)</strong>.
        </p>
      </div>

      {/* Two Column Comparison */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 16
      }}>
        {/* Left Card: Cottage Food */}
        <div style={{
          background: '#F8F9FA',
          borderRadius: 12,
          padding: 20,
          border: '1px solid #E9ECEF'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>🍪</span>
            <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: COLORS.PRIMARY_GRADIENT_START }}>
              Cottage Food (sell from home)
            </h3>
          </div>
          
          <ul style={{ margin: 0, paddingLeft: 20, color: '#222', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, lineHeight: 1.6 }}>
            {cottageFoodPoints.map((point, index) => (
              <li key={index} style={{ marginBottom: 12 }}>
                <span style={{ marginRight: 8 }}>{point.icon}</span>
                <span dangerouslySetInnerHTML={{
                  __html: point.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }} />
              </li>
            ))}
          </ul>
        </div>

        {/* Right Card: Not Cottage */}
        <div style={{
          background: '#F8F9FA',
          borderRadius: 12,
          padding: 20,
          border: '1px solid #E9ECEF'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>🍲</span>
            <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: COLORS.PRIMARY_GRADIENT_START }}>
              Not Cottage (perishable meals)
            </h3>
          </div>
          
          <ul style={{ margin: 0, paddingLeft: 20, color: '#222', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, lineHeight: 1.6 }}>
            {notCottagePoints.map((point, index) => (
              <li key={index} style={{ marginBottom: 12 }}>
                <span style={{ marginRight: 8 }}>{point.icon}</span>
                <span dangerouslySetInnerHTML={{
                  __html: point.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* What to Consider Next Section */}
      <div style={{
        background: '#F8F9FA',
        borderRadius: 12,
        padding: 20,
        border: '1px solid #E9ECEF'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING }}>✔</span>
          <h3 style={{ fontWeight: 600, fontSize: TYPOGRAPHY.ONBOARDING.STEP_HEADING, margin: 0, color: COLORS.PRIMARY_GRADIENT_START }}>
            What to consider next
          </h3>
        </div>
        
        <ul style={{ margin: 0, paddingLeft: 20, color: '#222', fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, lineHeight: 1.6 }}>
          {considerationsPoints.map((point, index) => (
            <li key={index} style={{ marginBottom: 12 }}>
              <span style={{ marginRight: 8 }}>{point.icon}</span>
              <span dangerouslySetInnerHTML={{
                __html: point.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
              }} />
            </li>
          ))}
        </ul>
      </div>

                   {/* Footer Section */}
             <div style={{ 
               display: 'flex', 
               flexDirection: 'column',
               gap: 16, 
               marginTop: 'auto',
               paddingTop: 24
             }}>
               <p style={{ color: ACCENT_GRAY, fontSize: TYPOGRAPHY.ONBOARDING.STEP_DESCRIPTION, margin: 0, lineHeight: 1.5 }}>
                 You can continue onboarding now. We'll save your progress and help you complete any legal steps before you start selling.
               </p>
               
               <div style={{ display: 'flex', gap: 12, justifyContent: 'space-between' }}>
                 {!isFirst && (
                   <button
                     type="button"
                     onClick={onBack}
                     style={{
                       background: '#fff',
                       color: '#9CA3AF',
                       border: 'none',
                       borderRadius: 10,
                       padding: '14px 32px',
                       fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                       fontWeight: 600,
                       cursor: 'pointer',
                       minHeight: 44,
                       minWidth: 120,
                       transition: 'all 0.2s',
                     }}
                     aria-label="Back"
                   >
                     Back
                   </button>
                 )}
                 
                 <button
                   type="button"
                   onClick={() => onNext && onNext({ foodType: 'not-sure' })}
                   style={{
                     background: COLORS.PRIMARY_GRADIENT,
                     color: '#fff',
                     border: 'none',
                     borderRadius: 10,
                     padding: '14px 32px',
                     fontSize: TYPOGRAPHY.ONBOARDING.BUTTON_TEXT,
                     fontWeight: 600,
                     cursor: 'pointer',
                     minHeight: 44,
                     boxShadow: '0 1px 4px #0001',
                     transition: 'background 0.2s',
                     marginLeft: 'auto',
                   }}
                   aria-label="Continue"
                 >
                   Continue
                 </button>
               </div>
             </div>
    </div>
  );
} 